import { useTranslation } from 'react-i18next';
/**
 * Load Cloud Family Dialog (Insert tab)
 *
 * Offline stand-in for the cloud library browser: a searchable list
 * over the built-in catalog. Selecting an entry registers the schema through
 * the same FamilyLoader path the Load Family tool uses, so loaded families
 * behave exactly like locally loaded ones.
 */

import { useMemo, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Box,
} from '@chakra-ui/react';
import { Search, DownloadCloud, FolderOpen, FileInput } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { getCloudFamilyCatalog } from '../../tools/LoadCloudFamilyTool';
import { familyLoader } from '../../core/services';
import { familyManager } from '../../core/FamilyManager';
import { importIfcFile } from '../../services/ifc/IfcFamilyImporter';

function toast(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
}

/** Open a native picker restricted to .ifc; resolves null on cancel. */
function pickIfcFile(): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.ifc';
        input.onchange = () => resolve(input.files?.[0] ?? null);
        input.oncancel = () => resolve(null);
        input.click();
    });
}

export function CloudFamilyDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'cloudFamily');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const [query, setQuery] = useState('');
    const [importing, setImporting] = useState(false);

    const catalog = useMemo(() => getCloudFamilyCatalog(), []);
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return catalog;
        return catalog.filter(f =>
            f.name.toLowerCase().includes(q) || f.category.toLowerCase().includes(q));
    }, [catalog, query]);

    const download = async (name: string) => {
        const schema = catalog.find(f => f.name === name);
        if (!schema) return;
        try {
            await familyLoader.loadFamily(schema);
            toast(`Family "${schema.name}" downloaded from the cloud library.`, 'success');
        } catch (err) {
            toast(`Download failed: ${String(err)}`, 'error');
        }
    };

    /** Import a local .ifc as static-mesh families (one per product). */
    const importIfc = async () => {
        const file = await pickIfcFile();
        if (!file) return;
        setImporting(true);
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const summary = await importIfcFile(file.name, bytes);
            if (summary.productCount === 0) {
                toast('No placeable products found in this IFC file.', 'warning');
            } else {
                toast(
                    summary.productCount === 1
                        ? `Imported 1 family from "${file.name}" — place it from the Project Browser.`
                        : `Imported ${summary.productCount} families from "${file.name}" — place them from the Project Browser.`,
                    'success'
                );
            }
        } catch (err) {
            toast(`IFC import failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
        } finally {
            setImporting(false);
        }
    };

    return (
        <ManageDialogFrame
            title={t('insert.loadCloudFamily')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <ManageDialogCloseFooter onClose={onClose} />
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack spacing={2}>
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none"><Search size={14} /></InputLeftElement>
                        <Input
                            placeholder={t('insert.searchTheCloudLibraryByFamilyNameOrCategory')}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </InputGroup>
                    <Button
                        size="sm"
                        leftIcon={<FileInput size={14} />}
                        isLoading={importing}
                        loadingText="Importing…"
                        onClick={() => void importIfc()}
                        flexShrink={0}
                    >{t('insert.importIFC')}</Button>
                </HStack>
                {results.length === 0 ? (
                    <Text fontSize="sm" color="var(--color-text-secondary)" py={4} textAlign="center">
                        No families match “{query}”.
                    </Text>
                ) : (
                    <Box overflowY="auto" maxH="46vh">
                        {results.map((f) => {
                            const loaded = familyManager.hasFamily(f.name);
                            return (
                                <HStack key={f.name} px={2} py={2} spacing={3} borderBottom="1px solid var(--color-border)">
                                    <Box color="var(--color-brand, #2f7bd6)">{loaded ? <FolderOpen size={18} /> : <DownloadCloud size={18} />}</Box>
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontSize="sm" fontWeight="medium">{f.name}</Text>
                                        <Text fontSize="xs" color="var(--color-text-secondary)">{f.category} · {f.types.length} type{f.types.length === 1 ? '' : 's'}</Text>
                                    </VStack>
                                    <Button
                                        size="xs"
                                        colorScheme="blue"
                                        isDisabled={loaded}
                                        onClick={() => void download(f.name)}
                                    >
                                        {loaded ? 'Loaded' : 'Download'}
                                    </Button>
                                </HStack>
                            );
                        })}
                    </Box>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
